import { motion } from "framer-motion";
import { DUR, EASE, VIEWPORT } from "../../lib/motion";
import "./TextReveal.css";

interface TextRevealProps {
    /**
     * Plain text only. Deliberately not `ReactNode`: this splits what it is
     * given into words and animates each one, which cannot be done to arbitrary
     * markup without either flattening it or walking the tree — and a heading
     * that needs nested markup is a heading that should be composed out of
     * several of these instead.
     */
    children: string;
    /** Seconds before the first word moves. */
    delay?: number;
    /** Applied to the wrapping element, so the caller keeps its own type styles. */
    className?: string;
    /** Rendered element. Headings should pass their real level. */
    as?: "h1" | "h2" | "h3" | "p" | "span";
    /**
     * Index of the first word to render in the muted tone, for the
     * `Featured Projects` / `Get In Touch` two-weight heading this site uses
     * throughout. Omitted, every word is full strength.
     *
     * A word index rather than a nested `<span>` because the split has to
     * happen here anyway — letting the caller pass markup would mean either
     * flattening their span back out or giving up on animating the words
     * inside it, and the muted half is exactly the half people read last.
     */
    mutedFrom?: number;
}

/** Gap between consecutive words. Tighter than the site's element STAGGER —
 *  words are small and read as one object, so the same spacing that feels
 *  considered between cards feels sluggish between them. */
const WORD_STAGGER = 0.045;

/**
 * A line of type that resolves word by word out of a blur.
 *
 * Distinct from MaskLine, which slides one whole line up from behind its
 * baseline and is reserved for the name — the site's single loudest moment.
 * This is the quieter relative used for section headings: the words arrive
 * slightly out of focus and settle, which reads as *text coming into
 * legibility* rather than an element sliding into place.
 *
 * The blur is the entire point. A pure translate-and-fade is the default that
 * every scroll library ships with, and it looks like it; focus resolving is a
 * thing eyes actually do, so it lands as motion the reader recognises rather
 * than as an animation they notice.
 */
export default function TextReveal({
    children,
    delay = 0,
    className,
    as = "span",
    mutedFrom,
}: TextRevealProps) {
    const Tag = motion[as];
    const words = children.split(" ");

    return (
        <Tag
            className={`text-reveal ${className ?? ""}`}
            initial="hidden"
            whileInView="shown"
            viewport={VIEWPORT}
            // Children read their timing from here, so a caller only ever has
            // to think about the line as a whole.
            variants={{
                hidden: {},
                shown: {
                    transition: {
                        delayChildren: delay,
                        staggerChildren: WORD_STAGGER,
                    },
                },
            }}
        >
            {words.map((word, i) => (
                // The trailing space has to live outside the animated span or
                // it gets blurred and translated along with the word, and the
                // gaps between words visibly breathe as the line resolves.
                <span
                    className={
                        "text-reveal-word" +
                        (mutedFrom !== undefined && i >= mutedFrom ? " is-muted" : "")
                    }
                    key={`${word}-${i}`}
                >
                    <motion.span
                        className="text-reveal-inner"
                        variants={{
                            hidden: { y: "0.42em", opacity: 0, filter: "blur(10px)" },
                            shown: {
                                y: "0em",
                                opacity: 1,
                                filter: "blur(0px)",
                                transition: { duration: DUR.slow, ease: EASE },
                            },
                        }}
                    >
                        {word}
                    </motion.span>
                    {i < words.length - 1 ? " " : ""}
                </span>
            ))}
        </Tag>
    );
}
